import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Spin, Alert, Modal, Table, Button, Radio, Tag, Tooltip, message } from "antd";
import {
  DownloadOutlined,
  PhoneOutlined,
  MailOutlined,
  WhatsAppOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ArrowRightOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  PersonAddAlt1,
  Storefront,
  EventAvailable,
  ShoppingCartCheckout,
  AccountBalance,
  WarningAmberRounded,
  CampaignRounded,
  PublishedWithChanges,
  Instagram,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Tooltip as RechartsTooltip,
} from "recharts";
import styled from "styled-components";
import customClientAxios from "../../customAxios";
import {
  PlatformAnalyticsCreatorFunnel,
  PlatformAnalyticsCreatorFunnelDrilldown,
  PlatformAnalyticsInstagramIntegrationStats,
  PlatformAnalyticsInstagramIntegrationDrilldown,
} from "../../utils/ApiEndpoints";
import { PageContainer, fmtNum, fmtRupees } from "../paUtils";

// Synthetic stageIds for the IG integration cards — let us reuse the funnel
// drilldown modal without forking it. Anything starting with "ig_" is an IG drill.
const IG_DRILL_STAGE_TO_TYPE = {
  ig_connected: "igConnected",
  ig_dm_automation: "usingDmAutomation",
  ig_crm: "usingCRM",
};

const IG_DRILL_LABELS = {
  ig_connected: "IG Connected — creators with Instagram linked to Growezy",
  ig_dm_automation: "Using DM Automation — creators with comment-to-DM live",
  ig_crm: "Using CRM — creators using the IG DM CRM",
};

const IG_NURTURE_TEMPLATES = {
  ig_connected:
    "Hi {name}, you've connected Instagram on Growezy — quick check-in: are you finding it useful, or any blockers we can help with?",
  ig_dm_automation:
    "Hi {name}, you're using DM Automation 🎉 Want a quick walkthrough on the AI brain features to get more out of it?",
  ig_crm:
    "Hi {name}, you're using our IG CRM. Quick question — anything you'd like easier or any reports you wish existed?",
};

// ─────────────────────────────────────────────────────────────────────────────
// Styled components
// ─────────────────────────────────────────────────────────────────────────────

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 28px;
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const HeroCard = styled.div`
  position: relative;
  background: ${(p) => p.$bg || "white"};
  color: ${(p) => (p.$dark ? "white" : "#1e293b")};
  border-radius: 16px;
  padding: 22px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06);
  border: 1px solid ${(p) => (p.$dark ? "transparent" : "#e2e8f0")};
  overflow: hidden;
  min-height: 130px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const HeroLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 600;
  color: ${(p) => (p.$dark ? "rgba(255,255,255,0.85)" : "#64748b")};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeroValue = styled.div`
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.7px;
  line-height: 1.1;
  margin-top: 4px;
`;

const HeroSub = styled.div`
  font-size: 12px;
  color: ${(p) => (p.$dark ? "rgba(255,255,255,0.85)" : "#94a3b8")};
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const DeltaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${(p) =>
    p.$direction === "up"
      ? "rgba(16, 185, 129, 0.15)"
      : p.$direction === "down"
      ? "rgba(239, 68, 68, 0.15)"
      : "rgba(148, 163, 184, 0.15)"};
  color: ${(p) =>
    p.$direction === "up" ? "#059669" : p.$direction === "down" ? "#dc2626" : "#64748b"};
`;

const SparklineWrap = styled.div`
  position: absolute;
  inset: auto 0 0 0;
  height: 50px;
  opacity: 0.55;
  pointer-events: none;
`;

// Funnel chart card
const FunnelCard = styled.div`
  background: white;
  border-radius: 18px;
  padding: 24px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 28px;
`;

const FunnelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const FunnelTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.3px;
`;

const FunnelHint = styled.p`
  margin: 4px 0 22px 0;
  font-size: 13px;
  color: #64748b;
  max-width: 720px;
`;

// Horizontal step flow
const StepFlow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0;
  overflow-x: auto;
  padding: 4px 0 12px 0;
`;

const StepWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1 1 0;
  min-width: 160px;
`;

const StepNode = styled.button`
  position: relative;
  background: ${(p) => p.$gradient};
  color: white;
  border: none;
  border-radius: 14px;
  padding: 18px 16px;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 4px 12px ${(p) => p.$shadow || "rgba(99, 102, 241, 0.25)"};
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 18px ${(p) => p.$shadow || "rgba(99, 102, 241, 0.35)"};
  }
`;

const StepIconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  svg {
    font-size: 20px;
    color: white;
  }
`;

const StepCount = styled.div`
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.5px;
  line-height: 1;
`;

const StepLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  margin-top: 6px;
  opacity: 0.95;
  line-height: 1.3;
`;

const StepFooter = styled.div`
  margin-top: 10px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.95;
`;

// Connector between steps
const Connector = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  min-width: 88px;
`;

const ConvBadge = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 4px 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const DropOffPill = styled.button`
  margin-top: 10px;
  background: ${(p) =>
    p.$urgency === "high"
      ? "linear-gradient(135deg, #fee2e2, #fecaca)"
      : p.$urgency === "med"
      ? "linear-gradient(135deg, #fef3c7, #fde68a)"
      : "#f1f5f9"};
  color: ${(p) =>
    p.$urgency === "high" ? "#991b1b" : p.$urgency === "med" ? "#92400e" : "#475569"};
  border: 1px solid
    ${(p) =>
      p.$urgency === "high" ? "#fecaca" : p.$urgency === "med" ? "#fde68a" : "#e2e8f0"};
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.3;
  transition: transform 0.15s ease;
  &:hover {
    transform: translateY(-1px);
  }
`;

// Featured priority callout — for special tracking metrics like "stuck published creators"
const PriorityCallout = styled.div`
  background: linear-gradient(135deg, #fef2f2 0%, #fff7ed 50%, #fffbeb 100%);
  border: 1px solid #fecaca;
  border-radius: 16px;
  padding: 22px;
  margin-bottom: 28px;
  display: flex;
  align-items: center;
  gap: 22px;
  box-shadow: 0 1px 3px rgba(220, 38, 38, 0.08);
  position: relative;
  overflow: hidden;
`;

const PriorityIconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
  svg { font-size: 28px; }
`;

const PriorityBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const PriorityHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
`;

const PriorityCount = styled.span`
  font-size: 36px;
  font-weight: 800;
  color: #b91c1c;
  letter-spacing: -1px;
  line-height: 1;
`;

const PriorityTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
`;

const PrioritySub = styled.div`
  font-size: 13px;
  color: #64748b;
  margin-top: 6px;
  line-height: 1.45;
`;

const PriorityActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

// Instagram Integration section
const IgSectionHeader = styled.div`
  margin-bottom: 12px;
`;

const IgSectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.3px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IgSectionSub = styled.p`
  margin: 4px 0 0 0;
  font-size: 13px;
  color: #64748b;
  max-width: 720px;
`;

const IgGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 28px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const IgCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 22px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 130px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  &:hover {
    transform: translateY(-2px);
    border-color: #cbd5e1;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06), 0 2px 4px rgba(0, 0, 0, 0.06);
  }
`;

const IgCardLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 600;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IgCardValue = styled.div`
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.7px;
  line-height: 1.1;
  color: #0f172a;
`;

const IgCardSub = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-top: auto;
`;

// Action center
const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 14px 0;
  letter-spacing: -0.2px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
`;

const ActionCard = styled.div`
  background: white;
  border-radius: 14px;
  border: 1px solid ${(p) => (p.$urgency === "high" ? "#fecaca" : "#e2e8f0")};
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  position: relative;
  overflow: hidden;
`;

const ActionUrgencyStripe = styled.div`
  position: absolute;
  inset: 0 auto 0 0;
  width: 4px;
  background: ${(p) =>
    p.$urgency === "high"
      ? "linear-gradient(180deg, #ef4444, #dc2626)"
      : p.$urgency === "med"
      ? "linear-gradient(180deg, #f59e0b, #d97706)"
      : "linear-gradient(180deg, #6366f1, #4f46e5)"};
`;

const ActionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ActionIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: ${(p) =>
    p.$urgency === "high"
      ? "linear-gradient(135deg, #fee2e2, #fecaca)"
      : p.$urgency === "med"
      ? "linear-gradient(135deg, #fef3c7, #fde68a)"
      : "linear-gradient(135deg, #e0e7ff, #c7d2fe)"};
  color: ${(p) =>
    p.$urgency === "high" ? "#dc2626" : p.$urgency === "med" ? "#d97706" : "#4f46e5"};
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    font-size: 20px;
  }
`;

const ActionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
`;

const ActionSubtitle = styled.div`
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
`;

const ActionBigNumber = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.6px;
  line-height: 1;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionBtn = styled.button`
  flex: 1;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: ${(p) => p.$primary ? "#1e293b" : "white"};
  color: ${(p) => p.$primary ? "white" : "#0f172a"};
  border: 1px solid ${(p) => p.$primary ? "#1e293b" : "#e2e8f0"};
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease;
  &:hover {
    background: ${(p) => p.$primary ? "#0f172a" : "#f8fafc"};
  }
  &:active {
    transform: translateY(1px);
  }
  svg {
    font-size: 14px;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const STAGE_ICON = {
  signed_up: PersonAddAlt1,
  brand_page_created: Storefront,
  event_created: EventAvailable,
  first_event_sale: ShoppingCartCheckout,
  sale_with_bank: AccountBalance,
  // Non-funnel tracker (used by drill-down only)
  published_upcoming_no_sale: PublishedWithChanges,
};

const STAGE_GRADIENT = {
  signed_up: { bg: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", shadow: "rgba(99,102,241,0.30)" },
  brand_page_created: { bg: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)", shadow: "rgba(59,130,246,0.30)" },
  event_created: { bg: "linear-gradient(135deg, #14b8a6 0%, #10b981 100%)", shadow: "rgba(16,185,129,0.30)" },
  first_event_sale: { bg: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", shadow: "rgba(239,68,68,0.30)" },
  sale_with_bank: { bg: "linear-gradient(135deg, #10b981 0%, #059669 100%)", shadow: "rgba(5,150,105,0.30)" },
};

const formatDate = (d) => {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

const csvEscape = (val) => {
  if (val === null || val === undefined) return "";
  const s = String(val);
  if (s.includes(",") || s.includes("\"") || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const downloadCSV = (rows, filename) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => csvEscape(r[h])).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const brandPageUrl = (slug) =>
  slug ? `https://${slug}.growezy.club` : null;

const NameLink = ({ name, slug, fallback = "—" }) => {
  const url = brandPageUrl(slug);
  if (!name && !slug) return <span style={{ color: "#94a3b8" }}>{fallback}</span>;
  if (!url) return <span>{name || fallback}</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "#4f46e5", fontWeight: 600, textDecoration: "none" }}
      title={`Open ${url}`}
    >
      {name || slug}
    </a>
  );
};

const computeDelta = (current, previous) => {
  if (previous == null) return null;
  if (previous === 0) return current > 0 ? { direction: "up", pct: null, label: "new" } : null;
  const pct = ((current - previous) / previous) * 100;
  return {
    direction: pct > 0.5 ? "up" : pct < -0.5 ? "down" : "flat",
    pct: Math.abs(pct),
    label: `${Math.abs(pct).toFixed(1)}%`,
  };
};

// Sparkline tooltip
const SparkTooltip = ({ active, payload, valueKey, formatter }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div
      style={{
        background: "rgba(15,23,42,0.92)",
        color: "white",
        padding: "6px 10px",
        borderRadius: 6,
        fontSize: 11,
      }}
    >
      <div style={{ opacity: 0.7 }}>{p.date}</div>
      <div style={{ fontWeight: 700 }}>{formatter ? formatter(p[valueKey]) : p[valueKey]}</div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Stage drop-off configs (urgency + nurture copy)
// ─────────────────────────────────────────────────────────────────────────────

const DROPOFF_CONFIGS = [
  {
    fromStage: "signed_up",
    toStage: "brand_page_created",
    title: "Signed up but never built a brand page",
    subtitle: "Complete onboarding to start hosting events",
    urgency: "med",
    icon: Storefront,
    nurture:
      "Hi {name}, welcome to Growezy! You signed up but haven't created your brand page yet. It only takes 2 minutes — would love to help you get set up.",
  },
  {
    fromStage: "brand_page_created",
    toStage: "event_created",
    title: "Onboarded but no event created",
    subtitle: "Help them ship their first event",
    urgency: "med",
    icon: EventAvailable,
    nurture:
      "Hi {name}, your brand page is live! Want help creating your first event? It takes ~5 minutes and we'll boost it for you.",
  },
  {
    fromStage: "event_created",
    toStage: "first_event_sale",
    title: "Created events but zero lifetime sales",
    subtitle: "Drafts, free events, or no buyers — marketing assist needed",
    urgency: "high",
    icon: WarningAmberRounded,
    nurture:
      "Hi {name}, you've created event(s) but haven't received any paid bookings yet. Let's chat — we can help with publishing, pricing, or a marketing nudge.",
  },
  {
    fromStage: "first_event_sale",
    toStage: "sale_with_bank",
    title: "Sales received but no bank account added",
    subtitle: "🚨 Blocked payouts — call immediately",
    urgency: "high",
    icon: AccountBalance,
    nurture:
      "Hi {name}, congrats on your first sale! 🎉 Please add your bank account so we can transfer your earnings. Reply 'YES' for help.",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

function FunnelTab({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Instagram integration stats (cohort-scoped)
  const [igStats, setIgStats] = useState(null);

  // Drill-down modal
  const [drillStage, setDrillStage] = useState(null);
  const [drillBucket, setDrillBucket] = useState("stuck");
  const [drillData, setDrillData] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // WhatsApp scaffold modal
  const [whatsAppModal, setWhatsAppModal] = useState(null); // { stage, recipients, template }

  const fetchFunnel = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customClientAxios().get(PlatformAnalyticsCreatorFunnel, {
        params: { startDate, endDate },
      });
      setData(res.data.data);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load funnel data");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchFunnel();
  }, [fetchFunnel]);

  // Fetch Instagram integration stats for the same cohort window
  useEffect(() => {
    let cancelled = false;
    customClientAxios()
      .get(PlatformAnalyticsInstagramIntegrationStats, {
        params: { startDate, endDate },
      })
      .then((res) => {
        if (!cancelled) setIgStats(res?.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setIgStats(null);
      });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const fetchDrilldown = useCallback(
    async (stageId, bucket) => {
      setDrillLoading(true);
      setDrillError(null);
      setSelectedRowKeys([]);
      try {
        const igType = IG_DRILL_STAGE_TO_TYPE[stageId];
        const res = igType
          ? await customClientAxios().get(PlatformAnalyticsInstagramIntegrationDrilldown, {
              params: { startDate, endDate, type: igType },
            })
          : await customClientAxios().get(PlatformAnalyticsCreatorFunnelDrilldown, {
              params: { startDate, endDate, stageId, bucket },
            });
        setDrillData(res.data.data.creators || []);
      } catch (e) {
        setDrillError(e?.response?.data?.error || "Failed to load drill-down");
        setDrillData([]);
      } finally {
        setDrillLoading(false);
      }
    },
    [startDate, endDate]
  );

  const openStageDrill = (stageId, bucket = "stuck") => {
    setDrillStage(stageId);
    setDrillBucket(bucket);
    fetchDrilldown(stageId, bucket);
  };

  const closeDrill = () => {
    setDrillStage(null);
    setDrillData([]);
    setDrillError(null);
    setSelectedRowKeys([]);
  };

  const handleBucketChange = (e) => {
    const bucket = e.target.value;
    setDrillBucket(bucket);
    if (drillStage) fetchDrilldown(drillStage, bucket);
  };

  const handleExport = () => {
    if (!drillData.length) return;
    const isIgDrill = !!IG_DRILL_STAGE_TO_TYPE[drillStage];
    const rows = drillData.map((c) => {
      const base = {
        "GMV (INR)": ((c.gmv || 0) / 100).toFixed(2),
        Name: c.name,
        Phone: c.phone,
        Email: c.email,
        "Brand Page": c.brandPageName || "",
        "Brand Page URL": c.brandPageSlug ? `https://${c.brandPageSlug}.growezy.club` : "",
      };
      if (isIgDrill) {
        base["IG Handle"] = c.igUsername ? `@${c.igUsername}` : "";
        base["IG Connected"] = formatDate(c.igConnectedAt);
      }
      return {
        ...base,
        "Signup Date": formatDate(c.signupDate),
        "Days Stuck": c.daysSinceSignup ?? "",
        "Onboarding": c.onboardingStatus || "",
        "Events (Total)": c.eventCount,
        "Events (Published)": c.liveEventCount ?? 0,
        "Events (Upcoming Published)": c.upcomingPublishedEventCount ?? 0,
        "Sales": c.saleCount,
        "Platform Fee (INR)": ((c.platformFee || 0) / 100).toFixed(2),
        "Last Sale": formatDate(c.lastSaleDate),
        "Bank Added": c.bankAdded ? "Yes" : "No",
        "Bank Verified": c.bankVerified ? "Yes" : "No",
        "KYC": c.kycStatus || "",
        "Last Login": formatDate(c.lastLoginAt),
      };
    });
    const safeStage = (drillStage || "stage").replace(/[^a-z0-9_-]/gi, "_");
    const filename = isIgDrill
      ? `ig_${safeStage}_${startDate}_to_${endDate}.csv`
      : `funnel_${safeStage}_${drillBucket}_${startDate}_to_${endDate}.csv`;
    downloadCSV(rows, filename);
  };

  const openWhatsAppFromDrill = () => {
    const recipients =
      selectedRowKeys.length > 0
        ? drillData.filter((c) => selectedRowKeys.includes(c.userId))
        : drillData;
    if (!recipients.length) {
      message.warning("No recipients to message");
      return;
    }
    const igTemplate = IG_NURTURE_TEMPLATES[drillStage];
    const cfg = DROPOFF_CONFIGS.find((c) => c.fromStage === drillStage);
    const igLabel = IG_DRILL_LABELS[drillStage]?.split("—")[0].trim();
    setWhatsAppModal({
      stageLabel:
        igLabel ||
        funnel.find((s) => s.stageId === drillStage)?.stage ||
        drillStage,
      recipients,
      template:
        igTemplate ||
        cfg?.nurture ||
        "Hi {name}, just checking in — let us know if we can help.",
    });
  };

  // ─── Computed ──────────────────────────────────────────────────────
  const funnel = useMemo(() => data?.funnel || [], [data]);
  const sideMetrics = data?.sideMetrics || {};
  const stuckMetrics = data?.stuckMetrics || {};
  const periodRevenue = data?.periodRevenue || {};
  const previousPeriodRevenue = data?.previousPeriodRevenue || null;
  const dailyTrend = data?.dailyRevenueTrend || [];

  const gmvDelta = computeDelta(periodRevenue.gmv, previousPeriodRevenue?.gmv);
  const feeDelta = computeDelta(periodRevenue.platformFee, previousPeriodRevenue?.platformFee);
  const salesDelta = computeDelta(periodRevenue.saleCount, previousPeriodRevenue?.saleCount);

  // Drop-off counts for action center
  const stageById = useMemo(
    () => Object.fromEntries(funnel.map((s) => [s.stageId, s])),
    [funnel]
  );
  const dropoffs = DROPOFF_CONFIGS.map((cfg) => {
    const from = stageById[cfg.fromStage]?.count || 0;
    const to = stageById[cfg.toStage]?.count || 0;
    const stuck = Math.max(from - to, 0);
    return { ...cfg, fromCount: from, toCount: to, stuck };
  });

  // ─── Drill-down columns ────────────────────────────────────────────
  // GMV first (most actionable revenue signal), then identity, then activity counts.
  const drillColumns = useMemo(
    () => [
      {
        title: "GMV",
        dataIndex: "gmv",
        key: "gmv",
        fixed: "left",
        width: 110,
        sorter: (a, b) => a.gmv - b.gmv,
        defaultSortOrder: "descend",
        render: (v) => (
          <span style={{ fontWeight: 700, color: v > 0 ? "#059669" : "#94a3b8" }}>
            {fmtRupees(v)}
          </span>
        ),
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        fixed: "left",
        width: 180,
        render: (v, r) => <NameLink name={v} slug={r.brandPageSlug} />,
      },
      {
        title: "Phone",
        dataIndex: "phone",
        key: "phone",
        width: 150,
        render: (v) =>
          v ? (
            <a href={`tel:${v}`}>
              <PhoneOutlined /> {v}
            </a>
          ) : (
            <span style={{ color: "#94a3b8" }}>—</span>
          ),
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        width: 200,
        render: (v) =>
          v ? (
            <a href={`mailto:${v}`}>
              <MailOutlined /> {v}
            </a>
          ) : (
            <span style={{ color: "#94a3b8" }}>—</span>
          ),
      },
      {
        title: "Brand Page",
        dataIndex: "brandPageName",
        key: "brandPageName",
        width: 170,
        render: (v, r) => {
          if (!v && !r.brandPageSlug) return <span style={{ color: "#94a3b8" }}>—</span>;
          const url = brandPageUrl(r.brandPageSlug);
          return (
            <Tooltip title={url ? `Open ${url}` : "(no slug)"}>
              {url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#0f172a" }}
                >
                  {v || r.brandPageSlug}
                </a>
              ) : (
                v
              )}
            </Tooltip>
          );
        },
      },
      ...(IG_DRILL_STAGE_TO_TYPE[drillStage]
        ? [
            {
              title: "IG Handle",
              dataIndex: "igUsername",
              key: "igUsername",
              width: 160,
              render: (v) =>
                v ? (
                  <a
                    href={`https://instagram.com/${v}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#E1306C" }}
                  >
                    @{v}
                  </a>
                ) : (
                  <span style={{ color: "#94a3b8" }}>—</span>
                ),
            },
            {
              title: "IG Connected",
              dataIndex: "igConnectedAt",
              key: "igConnectedAt",
              width: 120,
              render: (v) =>
                v ? formatDate(v) : <span style={{ color: "#94a3b8" }}>—</span>,
              sorter: (a, b) =>
                new Date(a.igConnectedAt || 0) - new Date(b.igConnectedAt || 0),
            },
          ]
        : []),
      {
        title: "Signed Up",
        dataIndex: "signupDate",
        key: "signupDate",
        width: 110,
        render: (v) => formatDate(v),
        sorter: (a, b) => new Date(a.signupDate || 0) - new Date(b.signupDate || 0),
      },
      {
        title: "Days Stuck",
        dataIndex: "daysSinceSignup",
        key: "daysSinceSignup",
        width: 110,
        sorter: (a, b) => (a.daysSinceSignup || 0) - (b.daysSinceSignup || 0),
        render: (v) => {
          if (v == null) return "-";
          let color = "default";
          if (v >= 30) color = "red";
          else if (v >= 14) color = "orange";
          else if (v >= 7) color = "gold";
          return <Tag color={color}>{v}d</Tag>;
        },
      },
      {
        title: "Events",
        dataIndex: "eventCount",
        key: "eventCount",
        width: 80,
        sorter: (a, b) => a.eventCount - b.eventCount,
        render: (v) => fmtNum(v),
      },
      {
        title: (
          <Tooltip title="Events with status live or published vs total events">
            Published / Total
          </Tooltip>
        ),
        dataIndex: "liveEventCount",
        key: "liveEventCount",
        width: 130,
        sorter: (a, b) => (a.liveEventCount || 0) - (b.liveEventCount || 0),
        render: (v, r) => {
          const live = v || 0;
          const total = r.eventCount || 0;
          if (total === 0) return <span style={{ color: "#94a3b8" }}>—</span>;
          if (live === 0) return <Tag color="red">0 / {total}</Tag>;
          return (
            <Tag color={live === total ? "green" : "blue"}>
              {live} / {total}
            </Tag>
          );
        },
      },
      {
        title: (
          <Tooltip title="Currently published events with a future event date — i.e. sellable inventory right now">
            Upcoming Published
          </Tooltip>
        ),
        dataIndex: "upcomingPublishedEventCount",
        key: "upcomingPublishedEventCount",
        width: 130,
        sorter: (a, b) =>
          (a.upcomingPublishedEventCount || 0) - (b.upcomingPublishedEventCount || 0),
        render: (v) => {
          const n = v || 0;
          if (n === 0) return <span style={{ color: "#94a3b8" }}>—</span>;
          return <Tag color="orange">{n}</Tag>;
        },
      },
      {
        title: "Sales",
        dataIndex: "saleCount",
        key: "saleCount",
        width: 80,
        sorter: (a, b) => a.saleCount - b.saleCount,
        render: (v) => fmtNum(v),
      },
      {
        title: "Bank",
        dataIndex: "bankAdded",
        key: "bankAdded",
        width: 100,
        render: (v, r) =>
          v ? (
            <Tag color={r.bankVerified ? "green" : "blue"}>
              {r.bankVerified ? "Verified" : "Added"}
            </Tag>
          ) : (
            <Tag color="red">None</Tag>
          ),
      },
    ],
    [drillStage]
  );

  // ─── Render ────────────────────────────────────────────────────────
  return (
    <PageContainer>
      {error && <Alert type="error" message={error} style={{ marginBottom: 20 }} />}

      <Spin spinning={loading}>
        {/* HERO REVENUE STRIP */}
        <HeroGrid>
          <HeroCard $bg="linear-gradient(135deg, #0f172a 0%, #1e293b 100%)" $dark>
            <HeroLabel $dark>
              <ShoppingCartCheckout fontSize="small" /> Total Event GMV
            </HeroLabel>
            <div>
              <HeroValue>{fmtRupees(periodRevenue.gmv)}</HeroValue>
              <HeroSub $dark>
                {gmvDelta && (
                  <DeltaPill $direction={gmvDelta.direction}>
                    {gmvDelta.direction === "up" ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {gmvDelta.label}
                  </DeltaPill>
                )}
                <span>vs prev period</span>
              </HeroSub>
            </div>
            {dailyTrend.length > 0 && (
              <SparklineWrap>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dailyTrend} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="gmvGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.6} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area
                      type="monotone"
                      dataKey="gmv"
                      stroke="#10b981"
                      strokeWidth={2}
                      fill="url(#gmvGrad)"
                    />
                    <RechartsTooltip
                      content={<SparkTooltip valueKey="gmv" formatter={fmtRupees} />}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </SparklineWrap>
            )}
          </HeroCard>

          <HeroCard>
            <HeroLabel>Platform Fee Earned</HeroLabel>
            <div>
              <HeroValue>{fmtRupees(periodRevenue.platformFee)}</HeroValue>
              <HeroSub>
                {feeDelta && (
                  <DeltaPill $direction={feeDelta.direction}>
                    {feeDelta.direction === "up" ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {feeDelta.label}
                  </DeltaPill>
                )}
                <span>our take</span>
              </HeroSub>
            </div>
          </HeroCard>

          <HeroCard>
            <HeroLabel>Total Event Sales</HeroLabel>
            <div>
              <HeroValue>{fmtNum(periodRevenue.saleCount)}</HeroValue>
              <HeroSub>
                {salesDelta && (
                  <DeltaPill $direction={salesDelta.direction}>
                    {salesDelta.direction === "up" ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {salesDelta.label}
                  </DeltaPill>
                )}
                <span>{fmtNum(periodRevenue.sellerCount)} unique sellers</span>
              </HeroSub>
            </div>
          </HeroCard>

          <HeroCard>
            <HeroLabel>New Signups (Cohort)</HeroLabel>
            <div>
              <HeroValue>{fmtNum(funnel[0]?.count || 0)}</HeroValue>
              <HeroSub>
                <DeltaPill $direction="flat">
                  {fmtNum(sideMetrics.onboardingPending || 0)} pending onboarding
                </DeltaPill>
              </HeroSub>
            </div>
          </HeroCard>
        </HeroGrid>

        {/* VISUAL FUNNEL */}
        <FunnelCard>
          <FunnelHeader>
            <div>
              <FunnelTitle>Creator Journey — New Signup Cohort</FunnelTitle>
              <FunnelHint>
                Click any step to see the creators inside it. Click a yellow/red drop-off
                pill to view who got stuck between two steps.{" "}
                <strong>Got Revenue</strong> = at least one paid event sale lifetime
                (free RSVPs excluded — they're not revenue). For creators stuck with
                upcoming published events but ₹0 lifetime revenue, see the priority
                callout below.
              </FunnelHint>
            </div>
          </FunnelHeader>

          <StepFlow>
            {funnel.map((stage, i) => {
              const Icon = STAGE_ICON[stage.stageId] || PersonAddAlt1;
              const grad = STAGE_GRADIENT[stage.stageId] || STAGE_GRADIENT.signed_up;
              const prev = i > 0 ? funnel[i - 1] : null;
              const conv = prev?.count
                ? Math.round((stage.count / prev.count) * 100)
                : null;
              const drop = prev ? Math.max(prev.count - stage.count, 0) : 0;
              const dropPct = prev?.count ? Math.round((drop / prev.count) * 100) : 0;
              const urgency =
                stage.stageId === "sale_with_bank" && drop > 0
                  ? "high"
                  : dropPct >= 40
                  ? "med"
                  : "low";

              return (
                <React.Fragment key={stage.stageId}>
                  {i > 0 && (
                    <Connector>
                      <ConvBadge>
                        <ArrowRightOutlined /> {conv != null ? `${conv}%` : "—"}
                      </ConvBadge>
                      {drop > 0 && (
                        <DropOffPill
                          $urgency={urgency}
                          onClick={() => openStageDrill(prev.stageId, "stuck")}
                          title={`Click to view the ${drop} stuck creators`}
                        >
                          <span style={{ fontSize: 14, fontWeight: 800 }}>
                            {fmtNum(drop)}
                          </span>
                          <span>stuck (-{dropPct}%)</span>
                        </DropOffPill>
                      )}
                    </Connector>
                  )}
                  <StepWrap>
                    <StepNode
                      $gradient={grad.bg}
                      $shadow={grad.shadow}
                      onClick={() => openStageDrill(stage.stageId, "at_stage")}
                      title="Click to view creators at this stage"
                    >
                      <StepIconWrap>
                        <Icon />
                      </StepIconWrap>
                      <StepCount>{fmtNum(stage.count)}</StepCount>
                      <StepLabel>{stage.stage}</StepLabel>
                      {stage.stageId === "first_event_sale" && (
                        <StepFooter>
                          <span>Cohort GMV: {fmtRupees(stage.cohortGMV)}</span>
                        </StepFooter>
                      )}
                    </StepNode>
                  </StepWrap>
                </React.Fragment>
              );
            })}
          </StepFlow>
        </FunnelCard>

        {/* INSTAGRAM INTEGRATION — cohort-scoped tracking */}
        <IgSectionHeader>
          <IgSectionTitle>
            <Instagram fontSize="small" style={{ color: "#E1306C" }} />
            Instagram Integration
          </IgSectionTitle>
          <IgSectionSub>
            How many creators in the cohort have integrated Instagram, and which features they're using.
            {igStats === null && " (loading…)"}
          </IgSectionSub>
        </IgSectionHeader>
        <IgGrid>
          {[
            { key: "igConnected", drillId: "ig_connected", label: "IG Connected" },
            { key: "usingDmAutomation", drillId: "ig_dm_automation", label: "Using DM Automation" },
            { key: "usingCRM", drillId: "ig_crm", label: "Using CRM" },
          ].map((card) => {
            const value = igStats?.[card.key];
            const total = igStats?.totalCreators;
            const pct = total ? Math.round(((value || 0) / total) * 100) : null;
            const clickable = (value || 0) > 0;
            return (
              <IgCard
                key={card.key}
                onClick={clickable ? () => openStageDrill(card.drillId, "at_stage") : undefined}
                style={!clickable ? { cursor: "default", opacity: 0.7 } : undefined}
                title={clickable ? "Click to view creators" : undefined}
              >
                <IgCardLabel>{card.label}</IgCardLabel>
                <IgCardValue>
                  {value === undefined ? "—" : fmtNum(value)}
                </IgCardValue>
                <IgCardSub>
                  {pct !== null
                    ? `${pct}% of ${fmtNum(total)} creators`
                    : "—"}
                </IgCardSub>
              </IgCard>
            );
          })}
        </IgGrid>

        {/* PRIORITY TRACKER — Published events with zero lifetime revenue */}
        {stuckMetrics.publishedUpcomingNoSale && (
          <PriorityCallout>
            <PriorityIconWrap>
              <PublishedWithChanges />
            </PriorityIconWrap>
            <PriorityBody>
              <PriorityHeader>
                <PriorityCount>
                  {fmtNum(stuckMetrics.publishedUpcomingNoSale.count)}
                </PriorityCount>
                <PriorityTitle>
                  creators with upcoming published events but{" "}
                  <span style={{ color: "#dc2626" }}>₹0 lifetime revenue</span>
                </PriorityTitle>
              </PriorityHeader>
              <PrioritySub>
                Their event is published with a future date — but they have never
                received a paid sale, on this event or any past event. These are
                top-priority outreach: help them market, price, or run a coupon push.
                ({fmtNum(stuckMetrics.publishedUpcomingNoSale.publishedUpcomingTotal)}{" "}
                creators have upcoming published events in total)
              </PrioritySub>
            </PriorityBody>
            <PriorityActions>
              <ActionBtn
                onClick={() =>
                  openStageDrill("published_upcoming_no_sale", "at_stage")
                }
              >
                <RightOutlined /> View list
              </ActionBtn>
              <ActionBtn
                $primary
                onClick={() =>
                  openStageDrill("published_upcoming_no_sale", "at_stage")
                }
              >
                <WhatsAppOutlined /> Nurture
              </ActionBtn>
            </PriorityActions>
          </PriorityCallout>
        )}

        {/* ACTION CENTER */}
        <SectionTitle>
          <CampaignRounded fontSize="small" /> Outreach Action Center
        </SectionTitle>
        <ActionGrid>
          {dropoffs.map((d) => {
            const Icon = d.icon;
            return (
              <ActionCard key={d.fromStage} $urgency={d.urgency}>
                <ActionUrgencyStripe $urgency={d.urgency} />
                <ActionHeader>
                  <ActionIcon $urgency={d.urgency}>
                    <Icon />
                  </ActionIcon>
                  <div>
                    <ActionTitle>{d.title}</ActionTitle>
                    <ActionSubtitle>{d.subtitle}</ActionSubtitle>
                  </div>
                </ActionHeader>
                <ActionBigNumber>{fmtNum(d.stuck)}</ActionBigNumber>
                <ActionRow>
                  <ActionBtn onClick={() => openStageDrill(d.fromStage, "stuck")}>
                    <RightOutlined /> View list
                  </ActionBtn>
                  <ActionBtn
                    $primary
                    onClick={() => {
                      openStageDrill(d.fromStage, "stuck");
                      // After list loads, the user can hit "Send WhatsApp" inside the drill.
                    }}
                  >
                    <WhatsAppOutlined /> Nurture
                  </ActionBtn>
                </ActionRow>
              </ActionCard>
            );
          })}
        </ActionGrid>
      </Spin>

      {/* DRILL-DOWN MODAL */}
      <Modal
        open={!!drillStage}
        onCancel={closeDrill}
        title={
          drillStage
            ? IG_DRILL_LABELS[drillStage] ||
              `${stageById[drillStage]?.stage || drillStage} — ${
                drillBucket === "stuck" ? "Stuck creators" : "All who reached this stage"
              }`
            : ""
        }
        width={1200}
        footer={null}
        destroyOnClose
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          {IG_DRILL_STAGE_TO_TYPE[drillStage] ? (
            <span style={{ fontSize: 13, color: "#64748b" }} />
          ) : (
            <Radio.Group value={drillBucket} onChange={handleBucketChange}>
              <Radio.Button value="stuck">Stuck (didn't progress)</Radio.Button>
              <Radio.Button value="at_stage">All at stage</Radio.Button>
            </Radio.Group>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, color: "#64748b" }}>
              {selectedRowKeys.length > 0
                ? `${selectedRowKeys.length} selected`
                : `${drillData.length} ${drillData.length === 1 ? "creator" : "creators"}`}
            </span>
            <Button
              icon={<DownloadOutlined />}
              disabled={!drillData.length}
              onClick={handleExport}
            >
              Export CSV
            </Button>
            <Button
              type="primary"
              icon={<WhatsAppOutlined />}
              disabled={!drillData.length}
              onClick={openWhatsAppFromDrill}
              style={{ background: "#25D366", borderColor: "#25D366" }}
            >
              Send WhatsApp Nurture
            </Button>
          </div>
        </div>
        {drillError && <Alert type="error" message={drillError} style={{ marginBottom: 12 }} />}
        <Table
          rowKey="userId"
          columns={drillColumns}
          dataSource={drillData}
          loading={drillLoading}
          pagination={{
            pageSize: 25,
            showSizeChanger: true,
            pageSizeOptions: ["25", "50", "100"],
          }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          scroll={{ x: 1400 }}
          size="small"
        />
      </Modal>

      {/* WHATSAPP NURTURE SCAFFOLD MODAL */}
      <Modal
        open={!!whatsAppModal}
        onCancel={() => setWhatsAppModal(null)}
        title={
          <span>
            <WhatsAppOutlined style={{ color: "#25D366" }} /> WhatsApp Broadcast —{" "}
            {whatsAppModal?.stageLabel}
          </span>
        }
        okText="Queue Broadcast (coming soon)"
        cancelText="Cancel"
        okButtonProps={{
          disabled: true,
          style: { background: "#25D366", borderColor: "#25D366" },
        }}
        onOk={() => {
          message.info("WhatsApp broadcast wiring is coming next — recipients are ready to send.");
          setWhatsAppModal(null);
        }}
        width={640}
      >
        {whatsAppModal && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                padding: 14,
              }}
            >
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
                Message template (use <code>{"{name}"}</code> placeholder):
              </div>
              <textarea
                defaultValue={whatsAppModal.template}
                style={{
                  width: "100%",
                  minHeight: 90,
                  border: "1px solid #cbd5e1",
                  borderRadius: 8,
                  padding: 10,
                  fontSize: 13,
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
              />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                Recipients ({whatsAppModal.recipients.length})
              </div>
              <div
                style={{
                  maxHeight: 200,
                  overflowY: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: "4px 0",
                }}
              >
                {whatsAppModal.recipients.slice(0, 50).map((r) => (
                  <div
                    key={r.userId}
                    style={{
                      padding: "6px 12px",
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: 12,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <NameLink
                      name={r.name || "(no name)"}
                      slug={r.brandPageSlug}
                      fallback="(no name)"
                    />
                    <span style={{ color: "#64748b" }}>{r.phone || "(no phone)"}</span>
                  </div>
                ))}
                {whatsAppModal.recipients.length > 50 && (
                  <div style={{ padding: "6px 12px", fontSize: 11, color: "#94a3b8" }}>
                    + {whatsAppModal.recipients.length - 50} more…
                  </div>
                )}
              </div>
            </div>
            <Alert
              type="info"
              showIcon
              message="WhatsApp broadcast integration is scaffolded but not yet live"
              description="The recipient list and template are ready. Wiring to your existing WhatsAppCampaign API (template approval + CSV upload + cron send) is the next step."
            />
          </div>
        )}
      </Modal>
    </PageContainer>
  );
}

export default FunnelTab;
